import {
  PLAN_EDIT_CLOSE_REASONS,
  PLAN_EDIT_ERROR_CODES
} from '@core/models/plan-edit.model';
import { readContextRefs } from '@core/services/gbe-error-parser/warning-normalizer';
import { toNaiveTimeInTz } from '@core/utils/time-utils';

const PREFIX = 'features.plan.edit';
const UNKNOWN = `${PREFIX}.errors.unknown`;

/** Codes the client raises itself, which no server answer carries. */
const CLIENT_ERROR_CODES = ['connection_lost', 'too_many_jobs'];

const COMPANY_BREACHED = 'plan_edit_route_company_breached';

/**
 * DL's details reach the store as sent, in snake_case; those the app builds from the camelCased
 * watch are camelCase, so both spellings are read.
 */
function detail(details: Record<string, unknown>, camel: string, snake: string): unknown {
  return details[camel] ?? details[snake];
}

type NamedKind = 'route' | 'company' | 'vehicle' | 'job';

/** Where DL's context puts each party of a company refusal. */
const BREACH_PARTIES: { kind: NamedKind; relation: string; entityType: string }[] = [
  { kind: 'route', relation: 'CULPRIT', entityType: 'ROUTE' },
  { kind: 'company', relation: 'LANDMARK', entityType: 'COMPANY' },
  { kind: 'vehicle', relation: 'REGULATOR', entityType: 'VEHICLE' },
  { kind: 'job', relation: 'REGULATOR', entityType: 'JOB' }
];

/** The uids a company refusal names, read from DL's context. */
export function breachUids(
  details: Record<string, unknown>
): Partial<Record<NamedKind, string>> {
  const refs = readContextRefs(details) ?? [];
  const uids: Partial<Record<NamedKind, string>> = {};
  for (const { kind, relation, entityType } of BREACH_PARTIES) {
    const ref = refs.find((r) => r.relation === relation && r.entityType === entityType);
    if (ref?.uid) uids[kind] = ref.uid;
  }
  return uids;
}

/** What a company refusal names: each label is the name the app knows, else the uid. */
function breachLabels(details: Record<string, unknown>): Record<NamedKind, string> {
  const uids = breachUids(details);
  const label = (kind: NamedKind) => {
    const name = details[`${kind}Name`];
    return typeof name === 'string' && name ? name : (uids[kind] ?? '');
  };
  return {
    route: label('route'),
    company: label('company'),
    vehicle: label('vehicle'),
    job: label('job')
  };
}

/**
 * The refusal says which vehicle or job it refused only when the route, the company and that
 * resource are all known; a sentence with a blank in it is worse than the plain message.
 */
function companyBreachedKey(details: Record<string, unknown>): string {
  const base = `${PREFIX}.errors.${COMPANY_BREACHED}`;
  const labels = breachLabels(details);
  if (!labels.route || !labels.company) return base;
  if (labels.vehicle) return `${base}_vehicle`;
  if (labels.job) return `${base}_job`;
  return base;
}

/**
 * Adds the names the app already holds for the parties of a company refusal. `lookup` gives a
 * known name for a uid, or undefined.
 */
export function withKnownNames(
  code: string,
  details: Record<string, unknown>,
  lookup: (kind: NamedKind, uid: string) => string | undefined
): Record<string, unknown> {
  if (code !== COMPANY_BREACHED) return details;
  const named: Record<string, unknown> = { ...details };
  for (const [kind, uid] of Object.entries(breachUids(details)) as [NamedKind, string][]) {
    const known = lookup(kind, uid);
    if (known) named[`${kind}Name`] = known;
  }
  return named;
}

export function planEditMessageKey(
  kind: 'error' | 'close',
  code: string,
  details: Record<string, unknown> = {}
): string {
  if (kind === 'close') {
    return PLAN_EDIT_CLOSE_REASONS.includes(code) ? `${PREFIX}.closed.${code}` : UNKNOWN;
  }
  if (code === COMPANY_BREACHED) return companyBreachedKey(details);
  return planEditHasOwnText(code) ? `${PREFIX}.errors.${code}` : UNKNOWN;
}

/** Whether GFE words the refusal itself; DL's own explanation is shown only for the others. */
export function planEditHasOwnText(code: string): boolean {
  return (
    code === COMPANY_BREACHED ||
    PLAN_EDIT_ERROR_CODES.includes(code) ||
    CLIENT_ERROR_CODES.includes(code)
  );
}

export function planEditMessageParams(
  code: string,
  details: Record<string, unknown>
): Record<string, unknown> {
  switch (code) {
    case 'plan_edit_action_time_before_predecessor': {
      const earliest = new Date(String(details['earliest']));
      const zone = details['siteTimeZone'];
      const timeZone =
        typeof zone === 'string' ? zone : Intl.DateTimeFormat().resolvedOptions().timeZone;
      return {
        earliest: isNaN(earliest.getTime())
          ? ''
          : toNaiveTimeInTz(earliest, timeZone).slice(0, 5)
      };
    }
    case 'plan_edit_plan_locked':
      return { holder: detail(details, 'holderName', 'holder_name') ?? '' };
    case COMPANY_BREACHED:
      return breachLabels(details);
    case 'plan_edit_quota_exceeded':
      return { scope: details['scope'] ?? '' };
    default:
      return {};
  }
}
